// ═════════════════════════════════════════════════════════════════════════════
//  THEME — light default, dark remembered per device (light-theme phase, T1)
// ═════════════════════════════════════════════════════════════════════════════
// docs/superpowers/specs/2026-08-05-light-theme-design.md: light is the new
// default everywhere; dark is a first-class option the device remembers
// (localStorage, not per-org). The mechanism is just an attribute stamp:
//
//   <html data-theme="dark">   -> css/{styles,admin,login}.css's
//                                  [data-theme="dark"] blocks take over
//   <html> (no attribute)      -> the plain :root blocks (light) apply
//
// Every shipped root HTML also carries its OWN 3-line inline copy of the
// storedTheme()+stamp logic in <head>, BEFORE the stylesheets load (anti-
// FOUC — this module isn't parsed yet at that point, esbuild bundles it into
// js/core.min.js which loads later). This module is the single source of
// truth for the same two rules everywhere else: what a page does once its
// scripts DO run (boot sync + the toggle button), and what the tests check.
const STORAGE_KEY = 'ordinexTheme';
// Light is the redesign's page background (--bg-0 / #F7F6F3): on a phone the
// browser paints its chrome with this, so a stale value shows as a seam above
// the app. Dark is untouched until a dark ramp is designed.
const META_COLOR = { dark: '#0a0b0e', light: '#F7F6F3' };

function normalize(theme) {
  return theme === 'dark' ? 'dark' : 'light';
}

// Live read of the current painted theme (not storedTheme() — that's the
// device's saved preference, this is what's on <html> right now, which is
// what a classic-script renderer needs when it bakes a literal hex into an
// HTML string instead of a CSS var). Used by field renderers (delivery.js,
// actions.js, equipment.js) that pick a status color per theme so dark stays
// pixel-identical to v485 while light gets an AA-safe variant.
export function isDark() {
  return document.documentElement.getAttribute('data-theme') === 'dark';
}

// What the device remembers. Absent key, a wiped/private-mode store, or any
// value that isn't literally 'dark' all fall back to light — never throws.
export function storedTheme() {
  let raw = null;
  try { raw = localStorage.getItem(STORAGE_KEY); } catch { /* private mode, etc. */ }
  return normalize(raw);
}

// Stamps the <html data-theme> attribute + flips the PWA chrome color
// (<meta name="theme-color">, if the page has one). Pure DOM sync — does NOT
// touch localStorage (see toggleTheme for the persisting half). Returns the
// normalized theme actually applied, so callers can trust it over their input.
export function applyTheme(theme) {
  const t = normalize(theme);
  document.documentElement.setAttribute('data-theme', t);
  try {
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', META_COLOR[t]);
  } catch { /* no head/meta yet — never fatal */ }
  return t;
}

// Flips whatever the page is currently showing (read from the live
// data-theme attribute, not storedTheme() — the two can differ for a beat
// right after boot sync), persists the new choice for this device, and
// applies it. Returns the new theme.
export function toggleTheme() {
  const current = normalize(document.documentElement.getAttribute('data-theme'));
  const next = current === 'dark' ? 'light' : 'dark';
  try { localStorage.setItem(STORAGE_KEY, next); } catch { /* private mode, etc. */ }
  return applyTheme(next);
}
